import { Localized, useLocalization } from '@fluent/react'
import TableContainer from '@mui/material/TableContainer'
import Paper from '@mui/material/Paper'
import Button from '@mui/material/Button'
import AddIcon from '@mui/icons-material/Add'
import { useAppDispatch, useAppSelector } from '../../app/hooks'
import { useMemo, useState } from 'react'
import { Box } from '@mui/material'
import { Skeleton } from '@mui/material'
import { LoadDeviceTypes, LoadURSs, LoadUsers } from '../../app/AppDataLoader'
import { selectAllStructure, selectStructureAndChildren } from '../device/structureRelationshipSlice'
import { loadUrsList, selectAllUrs } from '../urs/ursListSlice'
import { DataGrid, GridColDef, } from '@mui/x-data-grid'
import { plPL, enUS } from '@mui/x-data-grid/locales'
import { selectSettings } from '../settings/settingsSlice'
import { UrsHeader } from './UrsSlice'
import { useNavigate } from 'react-router-dom'
import { QuickSearchToolbar } from '../../app/QuickSearchToolbar'
import { getStructureName } from '../device/Structure'
import { DocumentFilterPanel } from '../documents/utils/DocumentFilterPanel'
import { selectModule } from '../modules/moduleSlice'
import { useLocalStorage } from '../../useLocalStorage'
import { URSListFilters } from '../documents/utils/types'
import { defURSFilters, filtersToQueryString } from '../documents/utils/helper'
import { selectContextStructure } from '../structure/contextStructureSlice'
import { LoadingContainer } from '../../app/LoadingContainer'
import { dynamicUrsFeatureFlag, selectHasFeatureFlag } from '../settings/settingsSlice'
import { CreateDynamicUrsDialog } from './CreateDynamicUrsDialog'
import { GoToButton } from '../../app/components/GoToButton'
import { DocumentStatusPill } from '../documents/controls/DocumentStatusPill'
import { Package } from '../permissions/usePackage'


export const UrsList = () => {
    const { l10n } = useLocalization()
    const { locale } = useAppSelector(selectSettings);
    const dispatch = useAppDispatch();

    const currentModule = useAppSelector(state => selectModule(state).currentModule)

    const [filteredUrs, setFilteredUrs] = useState<UrsHeader[]>([])
    const [useFilters, setUseFilters] = useState<boolean>(false);

    const [filters, setFilters] = useLocalStorage<URSListFilters>("URSListFilters", defURSFilters)
    const [createDynamicDialogOpen, setCreateDynamicDialogOpen] = useState(false)
    const f = useMemo(() => filtersToQueryString(filters), [filters])
    const hasDynamicUrsFeature = useAppSelector(selectHasFeatureFlag(dynamicUrsFeatureFlag))

    const allURSs =
        useAppSelector(state => selectAllUrs(state))
    const { data: structure } = useAppSelector(selectContextStructure)
    const relationship = useAppSelector(state => {
        if (structure && structure.mode === 'manual') {
            return selectStructureAndChildren(state, structure.structId)
        } else {
            return selectAllStructure(state)
        }
    })
    const URSs = useMemo(() => {
        if (useFilters) {
            return filteredUrs.filter(({ deviceTypeId }) => relationship.has(deviceTypeId));
        } else {
            return allURSs.filter(({ deviceTypeId }) => relationship.has(deviceTypeId));
        }
    }, [allURSs, relationship, filteredUrs])

    const handleGoToClick = (id, deviceTypeId) => {
        navigate(`/urs/edit/${id}?structureId=${deviceTypeId}`)

    };


    const columns: GridColDef<UrsHeader, any, any>[] = [
        {
            field: 'ursNo',
            headerName: l10n.getString("document-number", null, "_Document number"),
            minWidth: 130,
            flex: 0.85,
        },
        {
            field: 'version',
            headerName: l10n.getString("document-version", null, "_Document version"),
            width: 90,
            valueGetter: (_, row) => {
                return row.version
            }
        },
        {
            field: 'title',
            headerName: l10n.getString("document-title", null, "_Document title"),
            minWidth: 220,
            flex: 1.6,
        },
        {
            field: 'deviceTypeId',
            headerName: l10n.getString("structure", null, "_Structure"),
            minWidth: 180,
            flex: 1.2,
            valueGetter: (_, row) => {
                return getStructureName(row.deviceTypeId)
            }
        },
        {
            field: 'createdByFullName',
            headerName: l10n.getString("document-created-by", null, "_Author"),
            minWidth: 150,
            flex: 0.9,
        },
        {
            field: 'createDate',
            type: 'date',
            headerName: l10n.getString("document-created-date", null, "_Create date"),
            width: 150,
            valueGetter: (_, row) => new Date(row.createDate),
        },
        {
            field: 'status',
            headerName: l10n.getString("document-status", null, "_Status"),
            width: 150,
            valueGetter: (_, row) => row.status,
            renderCell: (params) => (
                <div>
                    <DocumentStatusPill status={params.row.status} size="small" maxWidth={160} />
                </div>
            ),
        },
        {
            field: 'actions', 
            type: 'actions', 
            width: 60,
            align: 'left',
            headerAlign: 'left',
            renderCell: (params) => (
                <Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-start' }}>
                    <GoToButton onClick={() =>
                        handleGoToClick(params.row.id, params.row.deviceTypeId)} />
                </Box>
            )
        }
    ]

    const addURL = "/urs/create"

    const getCustomLocaleText = () => {
        const defaultEnLocaleText = enUS.components.MuiDataGrid.defaultProps.localeText;

        if (locale === 'pl') {
            return plPL.components.MuiDataGrid.defaultProps.localeText;
        } else {
            return defaultEnLocaleText;
        }
    };

    const navigate = useNavigate()

    const handleRowDoubleClick = (params) => {
        if (currentModule !== null) {
            if (currentModule.code === 'offering') {
                navigate(`/supplier-awarded-urs/edit/${params.row.id}?structureId=${params.row.deviceTypeId}`)
                return
            }
            else {
                navigate(`/urs/edit/${params.row.id}?structureId=${params.row.deviceTypeId}`)
            }

        }
    };

    const handleAddClick = () => {
        navigate(addURL)
    }

    const handleAddDynamicClick = () => {
        setCreateDynamicDialogOpen(true)
    }

    const reload = () => {
        if (currentModule !== null) {
            dispatch(loadUrsList({ moduleId: currentModule.sysModuleId, filter: f }))
        }
    }

    return <>
        <DocumentFilterPanel filters={filters} onFilterChange={setFilters} reload={reload} />
        <LoadURSs component={<LoadingContainer />} filter={f}>
            <LoadUsers component={<LoadingContainer />}>
                <LoadDeviceTypes component={<Skeleton variant="rectangular" />}>
                    <TableContainer component={Paper}>
                        <DataGrid localeText={getCustomLocaleText()}
                            style={{ minHeight: 100, height: 'calc(100vh - 220px)' }}
                            rows={URSs} columns={columns}
                            initialState={{
                                sorting: { sortModel: [{ field: 'createDate', sort: 'desc' }] }
                            }}
                            slots={{ toolbar: QuickSearchToolbar }}
                            onRowDoubleClick={handleRowDoubleClick}
                        />
                        <Box sx={{
                            display: 'flex',
                            gap: 1,
                            p: 1,
                        }}>
                            <Package name="URS" access="ReadWrite">
                                <Button
                                    onClick={handleAddClick}
                                    color="secondary"
                                    variant="contained"
                                    size="small"
                                    startIcon={<AddIcon />}
                                >
                                    <Localized id="urs-create">New URS</Localized>
                                </Button>
                                {hasDynamicUrsFeature && (
                                    <Button
                                        onClick={handleAddDynamicClick}
                                        color="primary"
                                        variant="contained"
                                        size="small"
                                        startIcon={<AddIcon />}
                                    >
                                        <Localized id="urs-create-dynamic">New Dynamic URS</Localized>
                                    </Button>
                                )}
                            </Package>
                        </Box>
                    </TableContainer>

                    {hasDynamicUrsFeature && (
                        <CreateDynamicUrsDialog
                            open={createDynamicDialogOpen}
                            onClose={() => setCreateDynamicDialogOpen(false)}
                        />
                    )}

                </LoadDeviceTypes>
            </LoadUsers>
        </LoadURSs>
    </>
}
